import { Btn } from '../../../../shared/components/redesign';
import styles from './CropControls.module.css';

const ASPECT_CHIPS = ['Free', '3:2', '4:3', '16:9', '1:1'];

/**
 * CropControls — Tank Redesign crop accordion body.
 *
 * Vertical panel with five blocks (top-down):
 *   1. Aspect chips — Free / 3:2 / 4:3 / 16:9 / 1:1
 *   2. New Dimensions panel — `{w} × {h} px · {MP} MP`
 *   3. Origin X / Origin Y — read-only on first ship, source-pixel coords
 *   4. Rotation slider — −90..+90, step 0.5
 *   5. Straighten slider — −10..+10, "auto" label when Sentinel detected a roll
 *   6. Footer — Reset · Lock ratio · Apply to non-final
 */
export default function CropControls({
  // Aspect
  aspectLabel,
  availableAspects,
  onSetAspect,
  onCycleAspect,
  // Dimensions
  cropDims,
  cropBox,
  // Rotation
  cropRotation,
  onRotationChange,
  rollInfo,
  // Zoom
  zoom = 1,
  onZoomChange,
  // Lock / Apply
  lockRatio = false,
  onToggleLockRatio,
  // Reset / apply-to-non-final
  onResetCrop,
  onApplyToNonFinal,
  // Optional alternatives row (kept compatible with the prior toolbar)
  cropAlternatives,
  onApplyAlternative,
  // Greyed when not in crop mode — controls render but don't react.
  disabled = false,
}) {
  const cropW = cropDims?.w || 0;
  const cropH = cropDims?.h || 0;
  const mpRaw = (cropW * cropH) / 1_000_000;
  const mpLabel = mpRaw <= 0
    ? '—'
    : mpRaw >= 10
      ? `${mpRaw.toFixed(1)} MP`
      : `${mpRaw.toFixed(2)} MP`;

  // Always offer the universal set — the company profile narrows the
  // *automatic* default but the user must be able to override.
  void availableAspects;
  const aspectsToShow = ASPECT_CHIPS;

  const handleAspectClick = (label) => {
    if (typeof onSetAspect === 'function') {
      onSetAspect(label);
    } else if (typeof onCycleAspect === 'function') {
      // Best-effort fallback: cycle until label matches. This is async
      // (state batches) so it'll usually only advance one step per click,
      // but it preserves behaviour if `onSetAspect` isn't wired yet.
      onCycleAspect();
    }
  };

  // Detected roll state — drives the "auto" label on Straighten.
  const detectedAngle = rollInfo?.angle_deg ?? 0;
  const detectedConfidence = rollInfo?.confidence ?? 0;
  const detectionApplied = rollInfo?.applied === true;

  return (
    <div
      className={`${styles.panel} ${disabled ? styles.panelDisabled : ''}`}
      aria-disabled={disabled || undefined}
    >
      {/* Aspect chips */}
      <div className={styles.chipRow} role="radiogroup" aria-label="Aspect ratio">
        {aspectsToShow.map((label) => {
          const active = aspectLabel === label;
          return (
            <button
              key={label}
              type="button"
              role="radio"
              aria-checked={active}
              className={`${styles.chip} ${active ? styles.chipActive : ''}`}
              onClick={() => handleAspectClick(label)}
              disabled={disabled}
            >
              {label}
            </button>
          );
        })}
      </div>

      {/* New Dimensions */}
      <div className={styles.field}>
        <span className={styles.fieldLabel}>New Dimensions</span>
        <span className={styles.fieldValue}>
          {cropW > 0 && cropH > 0 ? (
            <>
              {cropW.toLocaleString()} × {cropH.toLocaleString()} px
              <span className={styles.dot}>·</span>
              {mpLabel}
            </>
          ) : '—'}
        </span>
      </div>

      {/* Origin X / Y — read-only on first ship */}
      <div className={styles.fieldGrid}>
        <div className={styles.field}>
          <span className={styles.fieldLabel}>Origin X</span>
          <span className={styles.fieldValue}>
            {cropBox?.x1 != null ? Math.round(cropBox.x1).toLocaleString() : '—'}
          </span>
        </div>
        <div className={styles.field}>
          <span className={styles.fieldLabel}>Origin Y</span>
          <span className={styles.fieldValue}>
            {cropBox?.y1 != null ? Math.round(cropBox.y1).toLocaleString() : '—'}
          </span>
        </div>
      </div>

      {/* Rotation slider */}
      <div className={styles.slider}>
        <div className={styles.sliderHeader}>
          <span className={styles.sliderLabel}>Rotation</span>
          <span className={styles.sliderValue}>
            {cropRotation >= 0 ? '+' : ''}{Number(cropRotation).toFixed(1)}°
          </span>
        </div>
        <input
          type="range"
          className={styles.sliderInput}
          min={-90}
          max={90}
          step={0.5}
          value={cropRotation || 0}
          onChange={(e) => onRotationChange?.(parseFloat(e.target.value))}
          disabled={disabled}
        />
      </div>

      {/* Zoom slider — drives the same zoom state as the wheel + +/- buttons.
          Range 1×–5× with two-decimal display. */}
      <div className={styles.slider}>
        <div className={styles.sliderHeader}>
          <span className={styles.sliderLabel}>Zoom</span>
          <span className={styles.sliderValue}>
            {Number(zoom || 1).toFixed(2)}×
            {detectionApplied && (
              <span className={styles.sliderHint}>
                {' '}auto-straightened {detectedAngle.toFixed(1)}°
              </span>
            )}
            {detectedConfidence > 0 && !detectionApplied && (
              <span className={styles.sliderHint}>
                {' '}detected roll {detectedAngle.toFixed(1)}°
              </span>
            )}
          </span>
        </div>
        <input
          type="range"
          className={styles.sliderInput}
          min={1}
          max={5}
          step={0.05}
          value={Math.max(1, Math.min(5, Number(zoom) || 1))}
          onChange={(e) => onZoomChange?.(parseFloat(e.target.value))}
          disabled={disabled}
        />
      </div>

      {/* Footer actions */}
      <div className={styles.footer}>
        <Btn kind="ghost" size="sm" onClick={onResetCrop} disabled={disabled || !onResetCrop}>
          Reset
        </Btn>
        <Btn
          kind={lockRatio ? 'soft' : 'ghost'}
          size="sm"
          onClick={onToggleLockRatio}
          disabled={disabled || !onToggleLockRatio}
        >
          {lockRatio ? 'Ratio locked' : 'Lock ratio'}
        </Btn>
        <Btn
          kind="ghost"
          size="sm"
          onClick={onApplyToNonFinal}
          disabled={disabled || !onApplyToNonFinal}
        >
          Apply to non-final
        </Btn>
      </div>

      {/* Optional alternative crops — collapsed under footer when present */}
      {cropAlternatives?.length > 0 && (
        <div className={styles.alternatives}>
          <span className={styles.fieldLabel}>Alt crops</span>
          <div className={styles.altRow}>
            {cropAlternatives.map((alt, i) => (
              <button
                key={alt.reason || i}
                type="button"
                className={styles.altBtn}
                onClick={() => onApplyAlternative?.(alt)}
              >
                {alt.reason ? alt.reason.replace(/_/g, ' ') : i + 1}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
